// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { gunzipSync, gzipSync } from 'fflate';
import type { Profile } from './projectkey';

/**
 * `mapping.enc` — the file that turns pseudonyms back into values.
 *
 *   off  len       field
 *     0    8       magic "SCRBJMAP"
 *     8    1       version = 1
 *     9    1       kdfId       1 = PBKDF2-HMAC-SHA256   (2 reserved for Argon2id)
 *    10    1       cipherId    1 = AES-GCM-256
 *    11    1       flags       bit0 payload gzipped
 *                              bit1 mapping contains raw credential values
 *                              bit2 project secret is sealed inside
 *    12    4       iterations, uint32 BE
 *    16   16       PBKDF2 salt, fresh on every write
 *    32   12       AES-GCM IV, fresh on every write
 *    44   32       key commitment
 *    76    4       metaLen, uint32 BE
 *    80   metaLen  cleartext metadata, UTF-8 JSON
 *  80+L   ...      AES-GCM ciphertext || 16-byte tag
 *
 * AAD is `bytes[0 .. 80+metaLen)`, so the whole header is authenticated.
 *
 * WHY A COMMITMENT. AES-GCM is not key-committing (Albertini et al., USENIX Security 2022): it
 * is possible to construct one ciphertext that decrypts successfully under two different keys.
 * The commitment closes that.
 *
 * WHAT THE COMMITMENT DOES *NOT* BUY. Because `prk` is a function of (passphrase, salt,
 * iterations), the commitment shadows the AAD for exactly the header fields the AAD is supposed
 * to protect — an iterations downgrade and a flipped commitment both surface identically. So the
 * error message is deliberately combined and must not be "improved" into a false promise:
 *
 *     Wrong passphrase — or this file's header has been altered.
 *
 * It also adds one small oracle: a 76-byte header fragment alone now permits an offline
 * dictionary attack where previously the ciphertext and tag were needed. That is disclosed.
 */

export const MAGIC = 'SCRBJMAP';
export const HEADER_FIXED = 80;
export const CONTAINER_VERSION = 1;
export const KDF_PBKDF2 = 1;
export const CIPHER_AES_GCM = 1;

export const FLAG_GZIP = 0x01;
export const FLAG_CONTAINS_SECRETS = 0x02;
export const FLAG_SEALS_PROJECT_KEY = 0x04;

/** Current OWASP guidance for PBKDF2-HMAC-SHA256. Stored in the header so raising it later
 *  does not orphan files written today. Measured ~52 ms on a desktop, ~420 ms budgeted on a phone. */
export const DEFAULT_ITERATIONS = 600_000;
export const MIN_ITERATIONS = 100_000;
/** Above this, a file you merely dropped could burn seconds of CPU before any authentication. */
export const MAX_ITERATIONS = 2_000_000;
export const SLOW_ITERATIONS = 1_000_000;
const MAX_META_LEN = 8192;

export interface MappingMeta {
  readonly tool: 'scrubjay';
  readonly v: number;
  readonly created: string;
  readonly keyFp: string;
  readonly profile: Profile;
  readonly tokenChars: number;
  readonly entries: number;
  readonly containsSecrets: boolean;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

function u32be(v: number): Uint8Array {
  const b = new Uint8Array(4);
  new DataView(b.buffer).setUint32(0, v, false);
  return b;
}

function readU32be(b: Uint8Array, off: number): number {
  return new DataView(b.buffer, b.byteOffset, b.byteLength).getUint32(off, false);
}

async function deriveParts(
  passphrase: string,
  salt: Uint8Array,
  iterations: number,
): Promise<{ encKey: CryptoKey; prk: CryptoKey }> {
  const base = await crypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, [
    'deriveBits',
  ]);
  // Derive 256 bits and split with HMAC. Deriving 512 in one call runs PBKDF2 twice — measured
  // 104 ms against 52 ms — for no additional security.
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' },
    base,
    256,
  );
  const prk = await crypto.subtle.importKey(
    'raw',
    bits,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const encRaw = await crypto.subtle.sign('HMAC', prk, enc.encode('scrubjay/v1/enc'));
  const encKey = await crypto.subtle.importKey('raw', encRaw, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
  return { encKey, prk };
}

async function commitmentFor(prk: CryptoKey, ctx: Uint8Array): Promise<Uint8Array> {
  const msg = new Uint8Array(enc.encode('scrubjay/v1/commit').length + ctx.length);
  const l = enc.encode('scrubjay/v1/commit');
  msg.set(l, 0);
  msg.set(ctx, l.length);
  return new Uint8Array(await crypto.subtle.sign('HMAC', prk, msg as BufferSource));
}

/** Everything the commitment covers: the header except the commitment field itself. */
function commitContext(container: Uint8Array, metaLen: number): Uint8Array {
  const head = container.subarray(0, 44);
  const tail = container.subarray(76, HEADER_FIXED + metaLen);
  const out = new Uint8Array(head.length + tail.length);
  out.set(head, 0);
  out.set(tail, head.length);
  return out;
}

/** Constant-time comparison. Not strictly required here, but free. */
function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}

export interface SealOptions {
  readonly containsSecrets: boolean;
  readonly sealProjectKey: boolean;
  readonly iterations?: number;
}

export async function sealMapping(
  payload: Uint8Array,
  meta: MappingMeta,
  passphrase: string,
  opts: SealOptions,
): Promise<Uint8Array> {
  const iterations = opts.iterations ?? DEFAULT_ITERATIONS;
  // gzip only if it actually shrinks: it inflates a two-entry mapping by ~20 bytes of framing.
  const gz = gzipSync(payload, { level: 6, mtime: 0 });
  const useGzip = gz.length < payload.length;
  const body = useGzip ? gz : payload;

  let flags = 0;
  if (useGzip) flags |= FLAG_GZIP;
  if (opts.containsSecrets) flags |= FLAG_CONTAINS_SECRETS;
  if (opts.sealProjectKey) flags |= FLAG_SEALS_PROJECT_KEY;

  const salt = new Uint8Array(16);
  crypto.getRandomValues(salt);
  const iv = new Uint8Array(12);
  crypto.getRandomValues(iv);

  const metaBytes = enc.encode(JSON.stringify(meta));
  if (metaBytes.length > MAX_META_LEN) throw new Error('mapping metadata too large');

  const header = new Uint8Array(HEADER_FIXED + metaBytes.length);
  header.set(enc.encode(MAGIC), 0);
  header[8] = CONTAINER_VERSION;
  header[9] = KDF_PBKDF2;
  header[10] = CIPHER_AES_GCM;
  header[11] = flags;
  header.set(u32be(iterations), 12);
  header.set(salt, 16);
  header.set(iv, 32);
  // bytes 44..76 are the commitment, filled in below
  header.set(u32be(metaBytes.length), 76);
  header.set(metaBytes, HEADER_FIXED);

  const { encKey, prk } = await deriveParts(passphrase, salt, iterations);
  const commitment = await commitmentFor(prk, commitContext(header, metaBytes.length));
  header.set(commitment, 44);

  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as BufferSource, additionalData: header as BufferSource },
      encKey,
      payload === body ? (payload as BufferSource) : (body as BufferSource),
    ),
  );

  const out = new Uint8Array(header.length + ct.length);
  out.set(header, 0);
  out.set(ct, header.length);
  return out;
}

/**
 * Parse and validate the header WITHOUT the passphrase.
 *
 * Order matters and is not negotiable:
 *   1. magic / version / algorithm ids
 *   2. clamp metaLen BEFORE touching it — an unbounded uint32 lets a 4 GB "mapping" OOM a phone
 *      inside JSON.parse
 *   3. length check BEFORE the KDF — otherwise we burn 52 ms of PBKDF2 on a container that has
 *      no ciphertext in it at all
 *   4. iterations bound
 *   5. JSON.parse inside try/catch
 *   6. schema-validate every field
 *
 * The caller renders this metadata with textContent only, never innerHTML, and labels it
 * "unverified until you enter the passphrase" — it is unauthenticated attacker-controlled text
 * being displayed in a document that holds AES keys and plaintext secrets.
 */
export function peekMeta(container: Uint8Array): { meta: MappingMeta; flags: number; iterations: number } {
  if (container.length < HEADER_FIXED + 16) throw new Error('Not a scrubjay mapping (too short).');
  if (dec.decode(container.subarray(0, 8)) !== MAGIC) {
    throw new Error('Not a scrubjay mapping (bad magic bytes).');
  }
  if (container[8] !== CONTAINER_VERSION) {
    throw new Error(`This mapping is version ${container[8]}; this build understands version 1.`);
  }
  if (container[9] !== KDF_PBKDF2) throw new Error('Unsupported key-derivation function.');
  if (container[10] !== CIPHER_AES_GCM) throw new Error('Unsupported cipher.');

  const flags = container[11]!;
  const iterations = readU32be(container, 12);
  const metaLen = readU32be(container, 76);

  if (metaLen > MAX_META_LEN) throw new Error('Corrupt header (metadata length out of range).');
  if (container.length < HEADER_FIXED + metaLen + 16) {
    throw new Error('Corrupt header (truncated — there is no ciphertext here).');
  }
  if (iterations < MIN_ITERATIONS || iterations > MAX_ITERATIONS) {
    throw new Error(`Corrupt header (iteration count ${iterations} is out of range).`);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(dec.decode(container.subarray(HEADER_FIXED, HEADER_FIXED + metaLen)));
  } catch {
    throw new Error('Not a scrubjay mapping (bad header).');
  }
  return { meta: validateMeta(parsed), flags, iterations };
}

function validateMeta(o: unknown): MappingMeta {
  const bad = (): never => {
    throw new Error('Not a scrubjay mapping (bad header).');
  };
  if (typeof o !== 'object' || o === null) bad();
  const m = o as Record<string, unknown>;
  if (m['tool'] !== 'scrubjay') bad();
  if (typeof m['v'] !== 'number' || !Number.isInteger(m['v'])) bad();
  if (typeof m['keyFp'] !== 'string' || !/^[0-9a-f]{8}$/.test(m['keyFp'])) bad();
  if (m['profile'] !== 'SJ1' && m['profile'] !== 'SJ2') bad();
  if (m['tokenChars'] !== 10 && m['tokenChars'] !== 13) bad();
  if (
    typeof m['entries'] !== 'number' ||
    !Number.isInteger(m['entries']) ||
    m['entries'] < 0 ||
    !Number.isFinite(m['entries'])
  ) {
    bad();
  }
  if (typeof m['created'] !== 'string' || Number.isNaN(Date.parse(m['created']))) bad();
  if (typeof m['containsSecrets'] !== 'boolean') bad();
  return {
    tool: 'scrubjay',
    v: m['v'] as number,
    created: m['created'] as string,
    keyFp: m['keyFp'] as string,
    profile: m['profile'] as Profile,
    tokenChars: m['tokenChars'] as number,
    entries: m['entries'] as number,
    containsSecrets: m['containsSecrets'] as boolean,
  };
}

export async function openMapping(
  container: Uint8Array,
  passphrase: string,
): Promise<{ payload: Uint8Array; meta: MappingMeta; flags: number }> {
  const { meta, flags, iterations } = peekMeta(container);
  const metaLen = readU32be(container, 76);
  const salt = container.subarray(16, 32);
  const iv = container.subarray(32, 44);
  const storedCommitment = container.subarray(44, 76);
  const header = container.subarray(0, HEADER_FIXED + metaLen);
  const ct = container.subarray(HEADER_FIXED + metaLen);

  const { encKey, prk } = await deriveParts(passphrase, salt, iterations);
  const expect = await commitmentFor(prk, commitContext(container, metaLen));
  if (!timingSafeEqual(expect, storedCommitment)) {
    throw new Error("Wrong passphrase — or this file's header has been altered.");
  }

  let plain: ArrayBuffer;
  try {
    plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as BufferSource, additionalData: header as BufferSource },
      encKey,
      ct as BufferSource,
    );
  } catch {
    throw new Error("Wrong passphrase — or this file's header has been altered.");
  }

  let payload = new Uint8Array(plain);
  if ((flags & FLAG_GZIP) !== 0) payload = gunzipSync(payload);
  return { payload, meta, flags };
}
